import React, { Component } from "react";
import Button, { BUTTON_DISPLAY } from "@acadia-ui/button";
import Heading, { HEADING_LEVEL, HEADING_SIZE } from "@acadia-ui/heading";
import Modal from "@acadia-ui/modal";
import { ToastNotification, TOAST_TYPE, showToast } from "@acadia-ui/toast";

import { onStateChange } from "events";
import ErrorHelpers from "../../Components/FormBuilder/ErrorHelpers";
import FormBuilderV2 from "../../Components/FormBuilder/FormBuilderV2";
import { putPackages, putPackagesAndPlacements } from "../../Flux/Actions/PackagesActions";
import { createPlacements, setFilteredPackage } from "../../Flux/Actions/PlacementActions";
import UpdateStateHelper from "../../Components/FormBuilder/UpdateStateHelper";
import ModalHelpers from "./ModalHelpers";
import PackagesDataStore from "../../Flux/Stores/PackagesDataStore";
import PlacementDataStore from "../../Flux/Stores/PlacementDataStore";
import WorkbookStore from "../../Flux/Stores/WorkbookStore";
import MasterCampaignsStore from "../../Flux/Stores/MasterCampaignsStore";
import BookFiltersStore from "../../Flux/Stores/BookFiltersStore";
import "./modal.scss";
import moment from "moment";
import Loading from "../../Components/LoadingSpinner";

const initialState = {
	formData: {},
	postData: {},
	errors: {},
	showErrors: false,
	valueToCalculate: "plannedTotalCost"
};

const formEl = "duplicate_package";

class DuplicatePackage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			metaData: {
				StartDate: moment(this.props.metaData.StartDate).format("YYYY/MM/DD"),
				EndDate: moment(this.props.metaData.EndDate).format("YYYY/MM/DD")
			},
			loading: false,
			...initialState
		};
	}

	componentDidMount() {
		this.props.duplicatePackage(this);
		PackagesDataStore.on("duplicate_packages_fail", this.handleErrors);
		PackagesDataStore.on("package__errors__reset", this.handleReset);
		PackagesDataStore.on("duplicate_packages_success", this.handleSuccess);
		PlacementDataStore.on("placement_failed", this.placementFailed);
		PlacementDataStore.on("placement_created", this.placementCreated);
	}

	componentWillUnmount() {
		this.props.duplicatePackage(undefined);
		PackagesDataStore.removeListener("duplicate_packages_fail", this.handleErrors);
		PackagesDataStore.removeListener("package__errors__reset", this.handleReset);
		PackagesDataStore.removeListener("duplicate_packages_success", this.handleSuccess);
		PlacementDataStore.removeListener("placement_failed", this.placementFailed);
		PlacementDataStore.removeListener("placement_created", this.placementCreated);
	}

	placementCreated = () => {
		showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.SUCCESS} message="Placements have been successfully created" timed={5000} />);
		this.props.navigate();
	};

	placementFailed = () => {
		this.props.refresh();
		showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.ERROR} message="Package was created but an error occured creating some placements." />);
		this.props.navigate();
	};

	// This is specifically for handling the scenario where placements have also been created alongside the package. We need to wait until the Placements data has been refetched
	// before we should close the modal and handle navigation
	placementsTabReady = () => {
		if (this.state.open) {
			this.setState({ loading: false }, () => {
				this.triggerOpenClose();
			});
		}
	};

	handleSuccess = async () => {
		if (this.props.duplicateData.ChildCount === "0") {
			await this.props.updateCounts("Package");
			await this.props.refresh();
			this.triggerOpenClose(null, true); // pass true here, don't wait for loading state to change.
			showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.SUCCESS} title="Package created" message="Package cloned successfully." timed={5000} />);
			// delay loading reset to next render cycle to avoid flicker
			setTimeout(() => {
				this.setState({ loading: false });
			}, 0);
		} else {
			const newPackage = PackagesDataStore.getNewPackage();
			const packageName = newPackage.PackageDescription ? newPackage.PackageDescription : newPackage.PackageName_Friendly;
			this.createDuplicatePlacements(newPackage.PackageID, packageName);
		}
	};

	handleErrors = () => {
		this.setState({ loading: false }, () => {
			const errors = PackagesDataStore.getErrors();
			if (Object.keys(errors).length > 0) {
				const placementErrors = errors;
				const type = "duplicate package";
				this.setState({ errors: placementErrors, showErrors: true }, () => {
					// console.log(this.state);
					ErrorHelpers.displayErrors(formEl, this.state.errors, type);
				});
			}
		});
	};

	/* Needed for all Modals */
	triggerOpenClose = (event, force = false) => {
		if (this.state.loading === false || force) {
			ModalHelpers.openClose(event, this.updateModalState, this.state, initialState, () => {
				ErrorHelpers.resetErrors(formEl);
				if (!this.state.open) {
					this.setState({ loading: true }, () => {
						this.fillFields();
					});
				}
			});
		}
	};

	handleParentReset = (callback) => {
		if (callback) {
			this.setState({ ...initialState }, callback());
		} else {
			this.setState({ ...initialState });
		}
	};

	fillFields = () => {
		this.handleParentReset();
		this.matchData();
	};

	matchData = () => {
		this.handleReset();
		const configObjs = Object.entries(this.props.config);
		const dataFieldsArray = [];
		for (const dataBucket of configObjs) {
			if (dataBucket[0] !== "ParentData" && dataBucket[0] !== "ChildData") {
				for (const field of dataBucket[1]) {
					if (this.props.duplicateData !== undefined) {
						if (this.props.duplicateData.hasOwnProperty(field.Name)) {
							setTimeout(() => {
								if (field.DataType === "Select") {
									let optionID = this.props.duplicateData[`${field.Name}ID`];

									const fieldItem = dataBucket[1].filter((a) => a.Name == field.Name);
									if (fieldItem.length > 0) {
										const optionItem = fieldItem[0].Options.filter((a) => a.OptionLabel == this.props.duplicateData[field.Name]);
										if (optionItem.length > 0) {
											optionID = optionItem[0].OptionID;
										}
									}

									// Populate Drop Down Fields
									// There are only 7 Parameters accepted: see updateState below to match
									if (optionID != undefined && optionID > 0) {
										dataFieldsArray.push([
											dataBucket[0],
											field.Name,
											{ value: optionID, label: this.props.duplicateData[field.Name] },
											field.ID,
											field.DataType,
											false,
											false
										]);
										// this.updateState(
										//     dataBucket[0],
										//     field.Name,
										//     { value: optionID, label: this.props.duplicateData[field.Name] },
										//     field.ID,
										//     field.DataType,
										//     false,
										//     false
										// );
									}
								} else {
									// Populate Free Text Fields
									// There are only 7 Parameters accepted: see updateState below to match
									dataFieldsArray.push([dataBucket[0], field.Name, this.props.duplicateData[field.Name], field.ID, field.DataType, false, false]);
									// this.updateState(
									//     dataBucket[0],
									//     field.Name,
									//     this.props.duplicateData[field.Name],
									//     field.ID,
									//     field.DataType,
									//     false,
									//     false
									// );
								}
							}, 0);
						}
					}
				}
			}
		}
		setTimeout(() => {
			this.updateStatev2(dataFieldsArray);
		}, 0);
	};

	updateModalState = () => {
		this.setState({ open: !this.state.open, ...initialState });
	};
	/* END */

	updateState = (dataConfig, name, value, id, dataType, calc, disabled) => {
		if (this.state.loading === false) {
			UpdateStateHelper.update(dataConfig, name, value, id, dataType, calc, formEl, this, disabled, this.props.config);
		}
	};

	updateStatev2 = (dataFieldsArray) => {
		UpdateStateHelper.updatev2(dataFieldsArray, formEl, this, this.props.config);
	};

	handleReset = () => {
		ErrorHelpers.resetErrors(formEl);
	};

	sortTaxonomyData = (taxonomyData) => {
		const taxDataWithPositions = taxonomyData;
		taxDataWithPositions.forEach((taxonomyField) => {
			if (this.props.config.TaxonomyData) {
				this.props.config.TaxonomyData.forEach((taxFieldConfig) => {
					if (taxFieldConfig.ID === taxonomyField.dataKeyID) {
						taxonomyField.Position = taxFieldConfig.Position;
					}
				});
			}
		});
		taxDataWithPositions.sort((a, b) => {
			if (a.Position < b.Position) {
				return -1;
			}
			if (a.Position > b.Position) {
				return 1;
			}
			return 0;
		});
		taxDataWithPositions.forEach((taxonomyField) => {
			delete taxonomyField.Position;
		});
		return taxDataWithPositions;
	};

	/* This should be a helper object that formats POST JSON for the PUT Actions */
	formDataObjForPost = (newRowObj) => {
		if (Object.keys(newRowObj).length > 0) {
			const postData = { ...this.state.postData };
			let strippedTaxonomyPostData = [];

			/* Removes the Key from the Taxonomy Data Array : Theja's Post does not like it */
			for (const i in postData.TaxonomyData) {
				strippedTaxonomyPostData.push(postData.TaxonomyData[i]);
			}
			strippedTaxonomyPostData = this.sortTaxonomyData(strippedTaxonomyPostData);

			const strippedMetadataPostData = [];
			for (const i in postData.MetaData) {
				strippedMetadataPostData.push(postData.MetaData[i]);
			}
			postData.MetaData = strippedMetadataPostData;

			/* postData is a copy of the state, so we just overwrite the innards with our new array */
			postData.TaxonomyData = strippedTaxonomyPostData;
			delete postData.RegEx;
			const filters = BookFiltersStore.getChoicesState();
			const campaignUniqueID = MasterCampaignsStore.getParentCampaignUniqueID();
			// const MasterUniqueCodeID = MasterCampaignsStore.getMasterUniqueID();
			if (postData.PackageData) {
				postData.PackageData.MasterCampaignID = this.props.metaData.MasterCampaignID;
				postData.PackageData.CampaignID = this.props.metaData.CampaignUniqueIDCode;
				if (this.props.metaData.Budget === null || this.props.metaData.Budget === 0) {
					postData.PackageData.Cost = 0;
				}
				postData.PackageData.PackageType = this.props.duplicateData.PackageType;
				// postData.PackageData.UniqueID = this.props.metaData.UniqueID;
			}
			/* Config will hold the ID's of the filters */
			const PlatformID = filters.platform.value;
			const AuditLevelID = this.props.auditLevelID;

			const contextValues = BookFiltersStore.getContextValues();
			const formSubmissionObj = {
				MasterAgencyDivisionName: contextValues?.agency?.name,
				MasterBusinessUnitName: contextValues?.client?.name,
				MasterCountryCode: contextValues?.country?.name,
				ContextID: contextValues.contextId,
				MasterRegionCode: contextValues?.region?.name,
				PlatformID,
				AuditLevelID,
				CampaignUniqueIDCode: campaignUniqueID,
				...postData
			};

			// console.log(formSubmissionObj);
			return [formSubmissionObj];
		}
		this.setState({ errors: { Errors: ["Please fill in all form fields."] }, showErrors: true }, () => {
			ErrorHelpers.displayErrors(formEl, this.state.errors);
		});
	};

	sendPostObject = (newRowObj) => {
		this.postObject = this.formDataObjForPost(newRowObj);
		if (this.postObject) {
			const updatedData = putPackages(this.postObject, false, true);
		}
		if (this.postObject === undefined) {
			this.setState({ loading: false });
		}
	};

	handleSubmit = (e) => {
		if (this.state.loading === false) {
			this.setState({ loading: true, closeModal: true }, () => {
				e.preventDefault();
				/* Server Side Validation */
				this.sendPostObject(this.state.formData);
			});
		}
	};

	checkForValidValue = (field, fieldValue) => {
		if (field.FieldType === "Date" || field.FieldType === "String") {
			return true;
		}
		if (field.FieldType === "Select" || field.DataType === "Select") {
			let optionExists = false;
			field.Options.forEach((placementOption) => {
				if (placementOption.OptionLabel === fieldValue) {
					optionExists = true;
				}
			});
			return optionExists;
		}
	};

	checkForValidDimensions = (field, fieldValue) => {
		if (field.FieldType === "Date" || field.FieldType === "String") {
			return true;
		}
		if (field.FieldType === "Select" || field.DataType === "Select") {
			let optionExists = false;
			field.Options.forEach((placementOption) => {
				if (placementOption.OptionLabel === fieldValue) {
					optionExists = true;
				}
			});
			return optionExists;
		}
	};

	createDuplicatePlacements = (packageID, packageDescription) => {
		PackagesDataStore.resetPlacementErrors();

		const placements = JSON.parse(JSON.stringify(PlacementDataStore.getData()));
		const filteredPlacements = placements.filter((placement) => {
			return placement.PackageID === this.props.duplicateData.PackageID;
		});
		const config = PlacementDataStore.getConfig();
		const parentData = PackagesDataStore.getNewPackage();
		filteredPlacements.forEach((placement) => {
			placement.CampaignID = null;
			placement.PlacementName = null;
			placement.TaxonomyData = null;
			placement.CreatedBy = null;
			placement.UpdatedBy = null;
			placement.UniqueID = null;
			placement.PackageID = packageID;
			// placement.Row = null;
			delete placement.PlacementID;
			if (placement.PlatformSiteID) {
				let siteID = null;
				config.PlacementData.forEach((placementConfig) => {
					if (placementConfig.Name === "PlatformSiteID") {
						placementConfig.Options.forEach((site) => {
							if (site.OptionLabel === placement.PlatformSiteID) {
								siteID = site.OptionLabel;
							}
						});
					}
				});
				if (siteID !== null) {
					placement.PlatformSiteID = siteID;
				}
			}
		});

		const inheritedData = {};
		for (const dataBucket of Object.entries(config)) {
			if (dataBucket[0] !== "ParentData") {
				for (const field of dataBucket[1]) {
					if (field.Name !== "Rate" && field.Name !== "PlannedUnits" && field.Name !== "Cost") {
						if (parentData.hasOwnProperty(field.Name) && field.Name !== "Size") {
							if (this.state.formData[field.Name]) {
								if (this.state.formData[field.Name].value !== undefined && this.state.formData[field.Name].value !== null) {
									let fieldValue = null;
									if (typeof this.state.formData[field.Name].value === "string") {
										if (field.Name === "StartDate" || field.Name === "EndDate") {
											fieldValue = moment(this.state.formData[field.Name].value).format("MM/DD/YYYY");
										} else {
											fieldValue = this.state.formData[field.Name].value;
										}
									} else if (typeof this.state.formData[field.Name].value === "object") {
										fieldValue = this.state.formData[field.Name].value.label;
									}
									if (fieldValue !== null && this.checkForValidValue(field, fieldValue)) {
										inheritedData[field.Name] = fieldValue;
									}
								}
							}
						} else if (field.Name === "Size") {
							if (parentData.hasOwnProperty("Dimensions")) {
								if (this.state.formData.Dimensions) {
									if (this.state.formData.Dimensions.value !== undefined && this.state.formData.Dimensions.value !== null) {
										let fieldValue = null;
										if (typeof this.state.formData.Dimensions.value === "string") {
											fieldValue = this.state.formData.Dimensions.value;
										} else if (typeof this.state.formData.Dimensions.value === "object") {
											fieldValue = this.state.formData.Dimensions.value.label;
										}
										if (fieldValue !== null && this.checkForValidDimensions(field, fieldValue)) {
											inheritedData.Size = fieldValue;
											inheritedData.PlacementSize = fieldValue;
											inheritedData.Dimensions = fieldValue;
										}
									}
								}
							}
						}
					}
				}
			}
		}

		// These are the only keys we want to inherit from Package to Placment now. Keeping the above block of code just in case we decide to revert this change
		const keysToInherit = ["InNameSite", "RateType"];

		Object.keys(inheritedData).forEach((inheritedField) => {
			if (keysToInherit.includes(inheritedField)) {
				filteredPlacements.forEach((filteredPlacement) => {
					filteredPlacement[inheritedField] = inheritedData[inheritedField];
				});
			}
		});

		const filters = BookFiltersStore.getChoicesState();
		let auditLevelID = 0;
		WorkbookStore.getAuditLevels().forEach((auditLevel) => {
			if (auditLevel.AuditLevelName === "Placement") {
				auditLevelID = auditLevel.AuditLevelID;
			}
		});
		const campaignUniqueID = MasterCampaignsStore.getParentCampaignUniqueID();
		const sheetPostObj = [];

		/* Config will hold the ID's of the filters */
		const PlatformID = filters.platform.value;
		const AuditLevelID = auditLevelID;

		for (const item in filteredPlacements) {
			if (filteredPlacements.hasOwnProperty(item)) {
				const lineItemFormattedObj = this.buildPostObjBuckets(filteredPlacements[item], item, packageID);
				// console.log('lineItemFormattedObj new', lineItemFormattedObj);
				if (filteredPlacements[item].UniqueID !== null) {
					lineItemFormattedObj.PlacementData.UniqueID = filteredPlacements[item].UniqueID;
				}
				if (filteredPlacements[item].PackageID !== null) {
					lineItemFormattedObj.PlacementData.PackageID = packageID;
				}
				if (filteredPlacements[item].PlacementID !== null) {
					lineItemFormattedObj.PlacementData.PlacementID = filteredPlacements[item].PlacementID;
				}
				lineItemFormattedObj.PlacementData.MasterCampaignID = filteredPlacements[item].MasterCampaignID || this.props.metaData.MasterCampaignID;

				lineItemFormattedObj.ParentData = { ParentUniqueIdCode: filteredPlacements[item].PackageID };
				if (lineItemFormattedObj.PlacementData.PlatformSiteID !== null) {
					let siteID = null;
					config.PlacementData.forEach((placementConfig) => {
						if (placementConfig.Name === "PlatformSiteID") {
							placementConfig.Options.forEach((site) => {
								if (site.OptionLabel === lineItemFormattedObj.PlacementData.PlatformSiteID) {
									siteID = site.OptionID;
								}
							});
						}
					});
					if (siteID !== null) {
						lineItemFormattedObj.PlacementData.PlatformSiteID = siteID;
					}
				}
				const contextValues = BookFiltersStore.getContextValues();
				const formSubmissionObj = {
					MasterAgencyDivisionName: contextValues?.agency?.name,
					MasterBusinessUnitName: contextValues?.client?.name,
					MasterCountryCode: contextValues?.country?.name,
					ContextID: contextValues.contextId,
					PlatformID,
					AuditLevelID,
					MasterRegionCode: contextValues?.region?.name,
					CampaignUniqueIDCode: campaignUniqueID
				};

				const mergedItems = Object.assign(formSubmissionObj, lineItemFormattedObj);
				sheetPostObj.push(mergedItems);
			}
		}
		this.filterChoices = BookFiltersStore.getChoicesState();
		this.parentCampaignUniqueID = MasterCampaignsStore.getParentCampaignUniqueID();
		const choices = JSON.parse(JSON.stringify(this.filterChoices));
		choices.auditlevel = { value: AuditLevelID };
		createPlacements(
			// this comment exists to prevent eslint from making this unreadable.
			sheetPostObj,
			choices,
			this.parentCampaignUniqueID,
			(clonedPlacements) => {
				this.props.setSelectedPackage(parentData);
				// setFilteredPackage([packageDescription, packageID], clonedPlacements, false);
			}
		);
	};

	buildPostObjBuckets = (lineItem, lineItemID, packageID) => {
		const headerConfigs = Object.entries(PlacementDataStore.getConfig());
		const postObj = {};
		// Create Iniital Bucket Arrays
		for (const dataBucket of headerConfigs) {
			if (dataBucket[0] !== "ParentData") {
				if (dataBucket[0] === "TaxonomyData" || dataBucket[0] === "MetaData") {
					postObj[`${dataBucket[0]}`] = [];
				} else {
					postObj[`${dataBucket[0]}`] = {};
				}
			}
		}
		for (const item in lineItem) {
			if (Object.hasOwn(lineItem, item)) {
				const itemKey = item;
				// Check all Buckets from the Config Obj
				for (const dataBucket of headerConfigs) {
					// Process all the data buckets except Parent Data.
					if (dataBucket[0] !== "ParentData") {
						// TaxonomyData or PlacementData
						for (const column of dataBucket[1]) {
							if (column.Name === itemKey.toString()) {
								// Based on DataType do stuff
								if (dataBucket[0] === "TaxonomyData" || dataBucket[0] === "MetaData") {
									if (lineItem[item]) {
										const reformattedValue = this.reformatTaxonomyValue(
											column.DataType,
											column.Options,
											column.Name,
											column.ID,
											lineItem[item] ? lineItem[item] : ""
										);
										postObj[`${dataBucket[0]}`].push(reformattedValue);
									}
								} else if (itemKey === "Rate" || itemKey === "PlannedUnits") {
									if (lineItem[item] !== "") {
										postObj[`${dataBucket[0]}`][`${itemKey}`] = lineItem[item];
									}
								} else {
									postObj[`${dataBucket[0]}`][`${itemKey}`] = lineItem[item];
								}
							}
						}
					}
				}
			}
			if (item === "ParentUniqueIdCode") {
				if (lineItem[item] !== null) {
					postObj.ParentData = { ParentUniqueIdCode: packageID };
				}
			}
		}
		postObj.PlacementData.UniqueID = lineItemID;
		return postObj;
	};

	reformatTaxonomyValue = (dataType, dataOptions, dataName, dataID, lineItemValue) => {
		if (dataType === "Select") {
			// Match the 'string' to the Options object to get ID
			const matchingOption = dataOptions.filter((option) => option.OptionLabel === lineItemValue.toString());
			if (matchingOption[0] !== undefined) {
				const lineItemID = matchingOption[0].OptionID;
				return {
					dataKeyID: dataID,
					dataValue: {
						dataValueID: lineItemID
					}
				};
			}
		} else {
			return {
				dataKeyID: dataID,
				dataValueIfFreeForm: lineItemValue
			};
		}
	};

	updateCalculatedField = (value) => {
		this.setState({ valueToCalculate: value });
	};

	render() {
		/* Temp Test Function -- Loads a skeleton if nothing there yet */
		let formInnards;
		if (this.props.config) {
			if (Object.keys(this.props.config).length !== 0) {
				formInnards = (
					<FormBuilderV2
						type="edit package"
						data={this.props.config}
						configName="packagesConfig"
						onUpdate={this.updateState}
						state={this.state}
						updateCalculatedField={this.updateCalculatedField}
						formEl={formEl}
						parentRef={this}
						metaData={this.props.metaData}
					/>
				);
			} else {
				formInnards = <div></div>;
			}
		}

		return (
			<>
				<Modal
					id={formEl}
					heading="Clone Package"
					size="medium"
					description="You can make edits inline, then click the ‘Clone’ button to duplicate the selected package."
					isOpen={this.state.open}
					onRequestClose={() => {
						this.setState({ open: false });
					}}
					onClick={() => {}}
					actionSetProps={{
						actionsRight: (
							<>
								<Button display="secondary" onClick={this.triggerOpenClose} data-tracking-id-alt="cancel-clone-package">
									Cancel
								</Button>
								<Button display="primary" onClick={this.handleSubmit} data-tracking-id-alt="confirm-clone-package">
									Clone
								</Button>
							</>
						)
					}}
				>
					{this.state.loading && <Loading className="modalLoading" />}
					<Heading as={HEADING_LEVEL.H2} size={HEADING_SIZE.EXTRA_SMALL} className="placement-count">
						Child placement count: {this.props.duplicateData?.ChildCount || 0}
					</Heading>
					<form autoComplete="off">
						<div className="form__group__container _m-t--10 _m-b--10">{formInnards}</div>
						{this.state.showErrors && <div id="errors__container" />}
					</form>
				</Modal>
			</>
		);
	}
}

export default DuplicatePackage;
